import { formatDisplayDateTime } from "../../utils/dateUtils";
import { ColumnDef } from "@tanstack/react-table";
import { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import DataTable2 from "shared/DataTable2";
import { useServerPaginatedList } from "../../shared/useServerPaginatedList";
import {
  ServerDeletedFilterValue,
  ServerStatusFilterValue,
} from "../../shared/ServerListFilters";
import ServerStatusPills from "../../shared/ServerStatusPills";
import ServerDeletedPills from "../../shared/ServerDeletedPills";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/Label";
import { Toaster } from "../../components/ui/toaster";
import { useToast } from "../../hooks/use-toast";
import { provider, Datum } from "../../interface/Provider";
import { providerAxiosInstance } from "../../Network/providerAxiosInstance";
import { ScrollArea } from "../../components/ui/scroll-area";
import { LoginAxiosInstance } from "../../Network/LoginAxiosInstance";
import FileUpload from "Pages/uploadFile/Provider/Provider";
import { Option } from "interface/Option";
import SpecialtyProviderSelector from "Pages/Shared/ProviderSpecialty";
import { SpecialtyAxiosInstance } from "../../Network/SpecialtyAxiosInstance";
import useQaAutomationIds from "../../utils/useQaAutomationIds";


// Linked record status does not change the parent record status.
const linkedInactive = (item: any): boolean => {
  if (!item || typeof item !== "object") return false;
  if (item.__inactive === true || item.is_deleted === true) return true;
  const state = item.is_active ?? item.Is_active ?? item.active ?? item.status ?? item.Status;
  return state === false || state === 0 || state === "0" ||
    ["false", "inactive", "disabled"].includes(String(state ?? "").trim().toLowerCase());
};
const responseRows = (response: any): any[] => {
  const raw = response?.data?.result ?? response?.data?.data ?? response?.data?.rows ?? response?.data;
  if (Array.isArray(raw)) return raw;
  for (const key of ["result", "data", "rows"]) if (Array.isArray(raw?.[key])) return raw[key];
  return [];
};
const fetchStatusCatalogue = async (client: any, endpoint: string): Promise<any[]> => {
  const result = new Map<string, any>();
  for (const active of ["true", "false"]) {
    for (let page = 1; page <= 100; page++) {
      const response = await client.get(endpoint, { params: { page, limit: 100, active, is_deleted: "false" } });
      const rows = responseRows(response);
      for (const item of rows) if (item?.id != null) result.set(String(item.id), item);
      const total = Number(response?.data?.pagination?.totalPages ?? response?.data?.totalPages);
      if (rows.length === 0 || (Number.isFinite(total) && total > 0 ? page >= total : rows.length < 100)) break;
    }
  }
  return Array.from(result.values());
};

const PROVIDER_API = {
  add: "addNewProvider",
  getAll: "getProvider",
  getById: (id: string | number) => `getProvider/${id}`,
  update: "updateProvider",
  restore: (id: string | number) => `restoreProvider/${id}`,
  softDelete: (id: string | number) => `softDeleteProvider/${id}`,
  hardDelete: (id: string | number) => `deleteProvider/${id}`,
  upload: "uploadFile",
} as const;

type BackendSpeciality = {
  id?: string | number;
  name: string;
  is_active?: boolean | string;
  status?: string;
  __inactive?: boolean;
};

const isActiveStatus = (value: any): boolean =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "Active" ||
  value === "active";

const normalizeSpecialities = (specs: any): BackendSpeciality[] => {
  if (specs == null || specs === "") return [];

  if (typeof specs === "string") {
    const trimmed = specs.trim();
    if (!trimmed) return [];

    try {
      return normalizeSpecialities(JSON.parse(trimmed));
    } catch {
      return trimmed
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean)
        .map((name) => ({ name }));
    }
  }

  if (Array.isArray(specs)) {
    return specs
      .flatMap((spec): BackendSpeciality[] => {
        if (spec == null) return [];

        if (typeof spec === "string" || typeof spec === "number") {
          const name = String(spec).trim();
          return name ? [{ name }] : [];
        }

        if (typeof spec === "object") {
          const name = String(
            spec.name ??
              spec.label ??
              spec.speciality ??
              spec.Speciality ??
              spec.specialty ??
              spec.Specialty ??
              "",
          ).trim();

          if (!name) return [];

          return [
            {
              ...spec,
              id: spec.id ?? spec.value,
              name,
            },
          ];
        }

        return [];
      })
      .filter((spec) => spec.name);
  }

  if (typeof specs === "object") {
    const name = String(
      specs.name ??
        specs.label ??
        specs.speciality ??
        specs.Speciality ??
        specs.specialty ??
        specs.Specialty ??
        "",
    ).trim();

    return name
      ? [
          {
            ...specs,
            id: specs.id ?? specs.value,
            name,
          },
        ]
      : [];
  }

  return [{ name: String(specs) }];
};

const getSpecialtyNames = (specs: any): string[] =>
  normalizeSpecialities(specs)
    .map((spec) => spec.name.trim())
    .filter(Boolean);

const getSpecialtyOptions = (specs: any): Option[] =>
  normalizeSpecialities(specs).map((spec) => ({
    value: spec.id != null ? String(spec.id) : spec.name,
    label: spec.name,
    __inactive: linkedInactive(spec),
    is_active: spec.is_active,
    status: spec.status,
  } as any));

const normalizeProvider = (item: any): Datum => {
  const rawStatus = item?.status ?? item?.Status;
  const rawSpeciality =
    item?.speciality ??
    item?.Speciality ??
    item?.specialty ??
    item?.Specialty;

  return {
    ...item,
    id: item?.id,
    provider_name: item?.provider_name ?? item?.Provider_name ?? "",
    provider_id: item?.provider_id ?? item?.Provider_id ?? "",
    speciality: normalizeSpecialities(rawSpeciality) as any,
    encounter_checks:
      item?.encounter_checks ?? item?.Encounter_checks ?? "OP",
    status: isActiveStatus(rawStatus) ? "Active" : "Inactive",
    is_deleted: Boolean(
      item?.is_deleted ?? item?.Is_deleted ?? item?.deleted ?? false,
    ),
    created_by:
      item?.created_by ?? item?.Created_by ?? item?.createdBy ?? null,
    createdat:
      item?.createdat ??
      item?.created_at ??
      item?.Created_at ??
      item?.createdAt ??
      null,
    updatedat:
      item?.updatedat ??
      item?.updated_at ??
      item?.Updated_at ??
      item?.updatedAt ??
      null,
    updated_by:
      item?.updated_by ?? item?.Updated_by ?? item?.updatedBy ?? [],
  } as Datum;
};

const normalizeProviderUniqueValue = (value: any): string =>
  String(value ?? "").trim().toLowerCase();

const extractProviderRows = (response: any): any[] => {
  const raw =
    response?.data?.result ??
    response?.data?.getFacilities ??
    response?.data?.data ??
    response?.data?.providers ??
    [];

  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.rows)) return raw.rows;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.result)) return raw.result;

  return [];
};

const extractProviderTotalCount = (
  response: any,
  fallbackCount: number,
): number => {
  const raw =
    response?.data?.totalCount ??
    response?.data?.pagination?.totalCount ??
    response?.data?.pagination?.total ??
    response?.data?.count;

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallbackCount;
};

const DUPLICATE_PROVIDER_NAME_MESSAGE =
  "This Provider Name already exists.";
const DUPLICATE_PROVIDER_ID_MESSAGE =
  "This Provider ID already exists.";

const PROVIDER_NAME_FORMAT_MESSAGE =
  "Invalid name. Only letters are allowed and each word must start with a capital letter.";

// Examples:
// Valid:   Al Noor Hospital, King-Fahad Clinic
// Invalid: 3494999, 43, al noor, Al noor, Asak aklwkelw
const PROVIDER_NAME_REGEX =
  /^[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*(?:\s+[A-Z][a-zA-Z]*(?:-[A-Z][a-zA-Z]*)*)*$/;

const getProviderNameValidationError = (value: string): string | undefined => {
  const trimmed = String(value ?? "").trim();

  if (!trimmed) return "Provider Name is required";
  if (trimmed.length < 3) return "Name must be at least 3 characters long";
  if (!PROVIDER_NAME_REGEX.test(trimmed)) return PROVIDER_NAME_FORMAT_MESSAGE;

  return undefined;
};

// Fetch the complete active provider dataset from the backend.
// This intentionally does NOT depend on the currently displayed table page,
// so duplicate validation works across every pagination page.
const fetchAllProvidersForDuplicateCheck = async (): Promise<Datum[]> => {
  const limit = 100;
  let page = 1;
  let rows: Datum[] = [];
  let reportedTotal = Number.NaN;

  // Safety guard prevents an accidental endless loop if the backend pagination
  // response is malformed.
  const maxPages = 1000;

  while (page <= maxPages) {
    const response = await providerAxiosInstance.get(PROVIDER_API.getAll, {
      params: {
        page,
        limit,
        is_deleted: "false",
      },
    });

    const pageRows = extractProviderRows(response).map((row: any) =>
      normalizeProvider(row),
    );

    if (pageRows.length === 0) break;

    rows = [...rows, ...pageRows];
    reportedTotal = extractProviderTotalCount(response, Number.NaN);

    if (Number.isFinite(reportedTotal) && rows.length >= reportedTotal) {
      break;
    }

    // If totalCount is not supplied, a short page normally means the last page.
    if (!Number.isFinite(reportedTotal) && pageRows.length < limit) {
      break;
    }

    page += 1;
  }

  return rows;
};

// ─── Role Helper ──────────────────────────────────────────────────────────────

const getUserRole = (): string => {
  try {
    const token = localStorage.getItem("token") || "";
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.userType || "";
  } catch {
    return "";
  }
};

const Provider: React.FC = () => {
  useQaAutomationIds("provider");
  const [items, setItems] = useState<provider>({ result: [] });
  const [specialtyCatalogue, setSpecialtyCatalogue] = useState<any[]>([]);
  const refreshSpecialtyCatalogue = async (): Promise<any[]> => {
    try {
      const rows = await fetchStatusCatalogue(SpecialtyAxiosInstance, "getSpecialty");
      setSpecialtyCatalogue(rows);
      return rows;
    } catch (error) {
      console.warn("Could not refresh Specialty statuses", error);
      return specialtyCatalogue;
    }
  };
  useEffect(() => { void refreshSpecialtyCatalogue(); }, []);
  const linkedSpecialties = (raw: any, catalogue: any[] = specialtyCatalogue): BackendSpeciality[] =>
    normalizeSpecialities(raw).map(spec => {
      const match = catalogue.find(row =>
        (spec.id != null && String(row.id) === String(spec.id)) ||
        String(row.name ?? row.specialty_name ?? row.speciality_name ?? row.Specialty_name ?? "").trim().toLowerCase() === spec.name.trim().toLowerCase()
      );
      return match ? { ...spec, is_active: match.is_active ?? match.active, status: match.status ?? match.Status, __inactive: linkedInactive(match) } : spec;
    });

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editIndex, setEditIndex] = useState<null | number>(null);
  const token = localStorage.getItem("token") || "";

  const userRole = getUserRole();
  const canManageDeleted =
    userRole === "system_admin" || userRole === "super_super_admin";

  const [selectedItem, setSelectedItem] = useState<Datum | null>(null);
  const [specialtyDialogOpen, setSpecialtyDialogOpen] = useState(false);
  const [selectedSpecialties, setSelectedSpecialties] = useState<string>("");
  const [filterName, setFilterName] = useState("");
  const [filterStatus, setFilterStatus] =
    useState<ServerStatusFilterValue>("");
  const [filterDeleted, setFilterDeleted] =
    useState<ServerDeletedFilterValue>("false");
  const [filterEncounter, setFilterEncounter] =
    useState<"" | "OP" | "IP" | "Both">("");
  const [gloableError, setGloableError] = useState<string>("");
  const { toast } = useToast();

  const [formName, setFormName] = useState("");
  const [formProviderId, setFormProviderId] = useState("");
  const [formSpecialty, setFormSpecialty] = useState<Option[]>([]);
  const [formEncounter, setFormEncounter] =
    useState<"OP" | "IP" | "Both">("OP");
  const [, setDialogContentEl] = useState<HTMLDivElement | null>(null);
  const [formActive, setFormActive] = useState(true);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    providerId?: string;
    specialty?: string;
  }>({});

  const [usersMap, setUsersMap] = useState<Record<number, string>>({});
  const [showAllUpdates, setShowAllUpdates] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Live validation for Provider Name + Provider ID.
  // Duplicate checks are performed against ALL backend pagination pages,
  // not only against items.result (the currently displayed page).
  useEffect(() => {
    const nameValue = formName.trim();
    const providerIdValue = formProviderId.trim();

    const normalizedName = normalizeProviderUniqueValue(nameValue);
    const normalizedProviderId =
      normalizeProviderUniqueValue(providerIdValue);

    const nameFormatError = nameValue
      ? getProviderNameValidationError(nameValue)
      : undefined;

    const providerIdFormatError =
      providerIdValue && !/^[a-zA-Z0-9]+$/.test(providerIdValue)
        ? "Provider ID must contain only letters and numbers"
        : undefined;

    // Clear stale duplicate errors immediately while the user changes a value,
    // while still showing local format validation without waiting for the API.
    setFormErrors((errors) => ({
      ...errors,
      name: nameFormatError,
      providerId: providerIdFormatError,
    }));

    const shouldCheckName =
      !!normalizedName && nameValue.length >= 3 && !nameFormatError;
    const shouldCheckProviderId =
      !!normalizedProviderId && !providerIdFormatError;

    if (!shouldCheckName && !shouldCheckProviderId) return;

    let cancelled = false;

    const timer = window.setTimeout(async () => {
      try {
        const allProviders = await fetchAllProvidersForDuplicateCheck();

        if (cancelled) return;

        const excludeId =
          editIndex === null ? null : Number(editIndex);

        const duplicateName =
          shouldCheckName &&
          allProviders.some(
            (item) =>
              (excludeId === null || Number(item.id) !== excludeId) &&
              !item.is_deleted &&
              normalizeProviderUniqueValue(item.provider_name) ===
                normalizedName,
          );

        const duplicateProviderId =
          shouldCheckProviderId &&
          allProviders.some(
            (item) =>
              (excludeId === null || Number(item.id) !== excludeId) &&
              !item.is_deleted &&
              normalizeProviderUniqueValue(item.provider_id) ===
                normalizedProviderId,
          );

        setFormErrors((errors) => ({
          ...errors,
          name: shouldCheckName
            ? duplicateName
              ? DUPLICATE_PROVIDER_NAME_MESSAGE
              : undefined
            : nameFormatError,
          providerId: shouldCheckProviderId
            ? duplicateProviderId
              ? DUPLICATE_PROVIDER_ID_MESSAGE
              : undefined
            : providerIdFormatError,
        }));
      } catch (error) {
        // Do not block typing if the live duplicate lookup temporarily fails.
        // The submit handler performs the same all-pages check again.
        console.error("Live provider duplicate validation failed:", error);
      }
    }, 500);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [formName, formProviderId, editIndex]);

  const clearForm = () => {
    setFormName("");
    setFormProviderId("");
    setFormSpecialty([]);
    setFormEncounter("OP");
    setFormActive(true);
    setFormErrors({});
    setGloableError("");
  };

  const handleAddClick = () => {
    setEditIndex(null);
    clearForm();
    setIsDialogOpen(true);
  };

  const fetchProviderDetails = async (
    id: string | number,
  ): Promise<Datum> => {
    const response = await providerAxiosInstance.get(PROVIDER_API.getById(id));

    const raw =
      response.data?.result ??
      response.data?.data ??
      response.data?.provider ??
      response.data;

    return normalizeProvider(raw);
  };

  const handleViewClick = async (item: Datum) => {
    setShowAllUpdates(false);

    try {
      const [details, catalogue] = await Promise.all([fetchProviderDetails(item.id), refreshSpecialtyCatalogue()]);
      setSelectedItem(normalizeProvider({ ...details, speciality: linkedSpecialties(details.speciality, catalogue) }));
    } catch (error) {
      console.error("Failed to fetch provider details:", error);
      setSelectedItem(normalizeProvider(item));

      toast({
        title: "Warning",
        description:
          "Provider details could not be refreshed. Showing the available row data.",
      });
    }
  };

  const handleEditClick = async (item: Datum) => {
    try {
      const [details, catalogue] = await Promise.all([fetchProviderDetails(item.id), refreshSpecialtyCatalogue()]);
      handleEdit(normalizeProvider({ ...details, speciality: linkedSpecialties(details.speciality, catalogue) }));
    } catch (error) {
      console.error("Failed to fetch provider details for edit:", error);
      handleEdit(normalizeProvider(item));
    }

    setIsDialogOpen(true);
  };

  const fetchUsers = async () => {
    try {
      const res = await LoginAxiosInstance.get("/");
      const raw: any[] =
        res.data?.users ?? res.data?.user ?? res.data ?? [];

      const map: Record<number, string> = {};

      raw.forEach((u: any) => {
        if (u.id != null) {
          map[Number(u.id)] =
            u.name ?? u.username ?? String(u.id);
        }
      });

      setUsersMap(map);
    } catch {}
  };

  const resolveUserName = (val: any): string => {
    if (val == null) return "—";
    if (typeof val === "object")
      return val.name ?? val.username ?? "—";
    if (typeof val === "string" && isNaN(Number(val))) return val;
    return usersMap[Number(val)] ?? String(val);
  };

  const {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalCount,
    allowedPageSizes,
    isTableLoading,
    manualPagination,
    fetchItems,
  } = useServerPaginatedList<
    Datum,
    {
      name: string;
      active: ServerStatusFilterValue;
      deleted: ServerDeletedFilterValue;
      encounter: "" | "OP" | "IP" | "Both";
    }
  >({
    filters: {
      name: filterName,
      active: filterStatus,
      deleted: filterDeleted,
      encounter: filterEncounter,
    },

    fetchPage: ({ page, limit, filters }) =>
      providerAxiosInstance.get(PROVIDER_API.getAll, {
        params: {
          page,
          limit,
          name: filters.name.trim() || undefined,
          active: filters.active || undefined,
          is_deleted: filters.deleted,
          encounter: filters.encounter || "all",
        },
      }),

    setRows: (rows) => setItems({ result: rows }),

    normalizeRow: (item: any) => normalizeProvider(item),

    sortRows: (rows) =>
      [...rows]
        .sort((a: any, b: any) => Number(b.id) - Number(a.id))
        .sort((a: any, b: any) =>
          a.is_deleted === b.is_deleted
            ? 0
            : a.is_deleted
              ? 1
              : -1,
        ),

    onError: (error) => {
      console.error("Error fetching items:", error);
      toast({
        title: "Error",
        description: "Failed to fetch Providers",
        variant: "destructive",
      });
    },
  });

  const validateProviderUniquenessAcrossAllPages = async (): Promise<{
    name: boolean;
    providerId: boolean;
  }> => {
    const normalizedName = normalizeProviderUniqueValue(formName);
    const normalizedProviderId =
      normalizeProviderUniqueValue(formProviderId);

    const excludeId =
      editIndex === null ? null : Number(editIndex);

    const allProviders = await fetchAllProvidersForDuplicateCheck();

    const duplicateName = allProviders.some(
      (item) =>
        (excludeId === null || Number(item.id) !== excludeId) &&
        !item.is_deleted &&
        normalizeProviderUniqueValue(item.provider_name) === normalizedName,
    );

    const duplicateProviderId = allProviders.some(
      (item) =>
        (excludeId === null || Number(item.id) !== excludeId) &&
        !item.is_deleted &&
        normalizeProviderUniqueValue(item.provider_id) === normalizedProviderId,
    );

    return {
      name: duplicateName,
      providerId: duplicateProviderId,
    };
  };

  const [forceAction, setForceAction] = useState<
    "softDelete" | "hardDelete" | "status" | null
  >(null);
  const [forceDialogOpen, setForceDialogOpen] = useState(false);
  const [forceId, setForceId] = useState<string | number | null>(null);
  const [forceError, setForceError] = useState<string>("");

  const toggleProviderStatus = async (
    item: Datum,
    force = false,
  ): Promise<void> => {
    try {
      const currentStatus = isActiveStatus(item.status);

      const url = force
        ? `${PROVIDER_API.update}?force=true`
        : PROVIDER_API.update;

      const updatedItem = {
        data: {
          Provider_name: item.provider_name,
          Provider_id: item.provider_id || "",
          Speciality: getSpecialtyNames(item.speciality),
          Encounter_checks: item.encounter_checks || "OP",
          Status: !currentStatus ? "Active" : "Inactive",
          id: item.id,
        },
        token,
      };

      const response = await providerAxiosInstance.put(
        url,
        updatedItem,
      );

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceError("");

        toast({
          title: "Success",
          description: `Status changed to ${
            !currentStatus ? "Active" : "Inactive"
          }.`,
        });
      } else {
        setForceId(item.id);
        setForceAction("status");
        setForceError(
          response.data.error ||
            response.data.message ||
            "This provider is used elsewhere.",
        );
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An unexpected error occurred.";

      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used")
      ) {
        setForceId(item.id);
        setForceAction("status");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: errMsg,
          variant: "destructive",
        });
      }
    }
  };

  const onSubmit = async (
    e: React.FormEvent,
  ): Promise<void> => {
    e.preventDefault();
    setGloableError("");

    const nameErr = getProviderNameValidationError(formName);

    const providerIdValue = formProviderId.trim();
    const providerIdErr = !providerIdValue
      ? "Provider ID is required"
      : !/^[a-zA-Z0-9]+$/.test(providerIdValue)
        ? "Provider ID must contain only letters and numbers"
        : undefined;

    const specialtyErr =
      formSpecialty.length === 0
        ? "At least one Specialty must be selected."
        : undefined;

    // Validate all required fields together and show only one popup message.
    // Individual field errors remain visible below their corresponding fields.
    setFormErrors((err) => ({
      ...err,
      name: nameErr,
      providerId: providerIdErr,
      specialty: specialtyErr,
    }));

    if (nameErr || providerIdErr || specialtyErr) {
      toast({
        title: "Error",
        description: "Please fill in all required fields before submitting.",
        variant: "destructive",
      });
      return;
    }

    try {
      const duplicateFields =
        await validateProviderUniquenessAcrossAllPages();

      if (duplicateFields.name || duplicateFields.providerId) {
        setFormErrors((errors) => ({
          ...errors,
          name: duplicateFields.name
            ? DUPLICATE_PROVIDER_NAME_MESSAGE
            : undefined,
          providerId: duplicateFields.providerId
            ? DUPLICATE_PROVIDER_ID_MESSAGE
            : undefined,
        }));

        const duplicateMessage =
          duplicateFields.name && duplicateFields.providerId
            ? "Provider Name and Provider ID already exist."
            : duplicateFields.name
              ? DUPLICATE_PROVIDER_NAME_MESSAGE
              : DUPLICATE_PROVIDER_ID_MESSAGE;

        // One consistent popup; the exact field-level duplicate message is also
        // shown directly below Provider Name and/or Provider ID.
        toast({
          title: "Error",
          description: duplicateMessage,
          variant: "destructive",
        });
        return;
      }

      const ValuesSpecialty = formSpecialty.map(
        (option) => option.label,
      );

      if (editIndex === null) {
        const newGroupArray = [
          {
            Provider_name: formName.trim(),
            Provider_id: formProviderId.trim(),
            Speciality: ValuesSpecialty,
            Encounter_checks: formEncounter,
            Status: formActive ? "Active" : "Inactive",
          },
        ];

        const payload = {
          data: newGroupArray,
          token,
        };

        const response =
          await providerAxiosInstance.post(
            PROVIDER_API.add,
            payload,
          );

        if (response.data.success) {
          if (currentPage === 0) {
            await fetchItems();
          } else {
            setCurrentPage(0);
          }

          clearForm();
          setIsDialogOpen(false);

          toast({
            title: "Success",
            description: "New Provider Added Successfully.",
          });
        } else {
          if (
            response.data.errors &&
            response.data.errors.length > 0
          ) {
            response.data.errors.forEach((err: any) => {
              const message =
                err.error ||
                err.message ||
                "Failed to add the provider.";

              setGloableError(message);

              toast({
                title: "Error",
                description: message,
                variant: "destructive",
              });
            });
          } else {
            const message =
              response.data.error ||
              response.data.message ||
              "Failed to add the provider.";

            setGloableError(message);

            toast({
              title: "Error",
              description: message,
              variant: "destructive",
            });
          }
        }
      } else {
        const updatedItem = {
          data: {
            Provider_name: formName.trim(),
            Provider_id: formProviderId.trim(),
            Speciality: ValuesSpecialty,
            Encounter_checks: formEncounter,
            Status: formActive ? "Active" : "Inactive",
            id: editIndex,
          },
          token,
        };

        const response =
          await providerAxiosInstance.put(
            PROVIDER_API.update,
            updatedItem,
          );

        if (response.data.success) {
          await fetchItems();

          clearForm();
          setIsDialogOpen(false);

          toast({
            title: "Success",
            description: "Provider updated successfully.",
          });
        } else {
          const message =
            response.data.error ||
            response.data.message ||
            "Failed to update the provider.";

          setGloableError(message);

          toast({
            title: "Error",
            description: message,
            variant: "destructive",
          });
        }
      }
    } catch (error: AxiosError | any) {
      const message =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "An unexpected error occurred.";

      setGloableError(message);

      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    }
  };

  const handleEdit = (item: Datum) => {
    const normalized = normalizeProvider(item);

    setFormName(normalized.provider_name || "");
    setFormProviderId(normalized.provider_id || "");

    setFormSpecialty(
      getSpecialtyOptions(linkedSpecialties(normalized.speciality)),
    );

    setFormEncounter(
      (normalized.encounter_checks as "OP" | "IP" | "Both") ||
        "OP",
    );

    setFormActive(isActiveStatus(normalized.status));
    setFormErrors({});
    setEditIndex(normalized.id);
  };

  const handleDownload = (item: Datum) => {
    const normalized = normalizeProvider(item);
    const specialtyNames =
      getSpecialtyNames(normalized.speciality).join(" | ");

    const rows = [
      "id;provider_name;provider_id;speciality;encounter_checks;status",
    ];

    rows.push(
      [
        normalized.id || "",
        normalized.provider_name || "",
        normalized.provider_id || "",
        specialtyNames,
        normalized.encounter_checks || "",
        isActiveStatus(normalized.status) ? "TRUE" : "FALSE",
      ].join(";"),
    );

    const csvContent = rows.join("\n");
    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `Provider${normalized.id}.csv`;
    a.click();

    window.URL.revokeObjectURL(url);
  };

  const removeItem = async (
    id: string | number,
  ): Promise<void> => {
    try {
      const response = await providerAxiosInstance.delete(
        PROVIDER_API.softDelete(id),
        {
          data: { token },
        },
      );

      if (response.data.success) {
        await fetchItems();

        toast({
          title: "Success",
          description: "Provider Deleted Successfully.",
        });
      } else {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(
          response.data.error ||
            response.data.message ||
            "This provider is used elsewhere.",
        );
        setForceDialogOpen(true);
      }
    } catch (error: any) {
      const errMsg =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to delete Provider";

      if (
        error.response?.status === 409 ||
        errMsg.toLowerCase().includes("used")
      ) {
        setForceId(id);
        setForceAction("softDelete");
        setForceError(errMsg);
        setForceDialogOpen(true);
      } else {
        toast({
          title: "Error",
          description: errMsg,
          variant: "destructive",
        });
      }
    }
  };

  const restoreItem = async (
    id: string | number,
  ): Promise<void> => {
    try {
      const response = await providerAxiosInstance.patch(
        PROVIDER_API.restore(id),
        { token },
      );

      if (response.data.success) {
        await fetchItems();

        toast({
          title: "Success",
          description: "Provider restored successfully.",
        });
      } else {
        toast({
          title: "Error",
          description:
            response.data.error ||
            response.data.message ||
            "Failed to restore Provider",
          variant: "destructive",
        });
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to restore Provider",
        variant: "destructive",
      });
    }
  };

  const permanentDeleteItem = async (
    id: string | number,
    force = false,
  ): Promise<void> => {
    try {
      const url = force
        ? `${PROVIDER_API.hardDelete(id)}?force=true`
        : PROVIDER_API.hardDelete(id);

      const response = await providerAxiosInstance.delete(url, {
        data: { token },
      });

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);

        toast({
          title: "Success",
          description: "Provider Permanently Deleted.",
        });
      } else {
        setForceAction("hardDelete");
        setForceError(
          response.data.error ||
            response.data.message ||
            "This provider is used elsewhere.",
        );
      }
    } catch (error: any) {
      setForceAction("hardDelete");
      setForceError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to delete Provider",
      );
    }
  };

  const forceSoftDelete = async (
    id: string | number,
  ): Promise<void> => {
    try {
      const response = await providerAxiosInstance.delete(
        `${PROVIDER_API.softDelete(id)}?force=true`,
        {
          data: { token },
        },
      );

      if (response.data.success) {
        await fetchItems();
        setForceDialogOpen(false);
        setForceId(null);
        setForceError("");
        setForceAction(null);

        toast({
          title: "Success",
          description: "Provider force Deleted Successfully.",
        });
      } else {
        setForceError(
          response.data.error ||
            response.data.message ||
            "Failed to force delete Provider",
        );
      }
    } catch (error: any) {
      setForceError(
        error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to force delete Provider",
      );
    }
  };

  const columns: ColumnDef<Datum>[] = [
    {
      accessorKey: "id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          ID
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <span className="block text-center font-medium">
          {currentPage * pageSize + row.index + 1}
        </span>
      ),
    },
    {
      accessorKey: "provider_name",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          Provider Name
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const isDeleted = row.original.is_deleted;

        return (
          <span
            className={`block text-center font-medium ${
              isDeleted
                ? "line-through text-gray-400"
                : ""
            }`}
          >
            {row.original.provider_name}
          </span>
        );
      },
    },
    {
      accessorKey: "provider_id",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          Provider ID
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <span className="block text-center font-medium">
          {row.original.provider_id}
        </span>
      ),
    },
    {
      accessorKey: "speciality",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          Speciality
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const specialtyEntries = linkedSpecialties(row.original.speciality);
        const specialtyArray = specialtyEntries.map(spec => spec.name);

        if (specialtyArray.length === 0) {
          return (
            <span className="text-gray-400 block text-center">
              -
            </span>
          );
        }

        const searchTerm = filterName
          .toLowerCase()
          .trim();
        const hasSearch = searchTerm.length >= 2;

        const matchingSpecialties = hasSearch
          ? specialtyArray.filter((specialty) =>
              specialty
                .toLowerCase()
                .includes(searchTerm),
            )
          : [];

        const hasMatches =
          hasSearch && matchingSpecialties.length > 0;

        const displaySource = hasMatches
          ? matchingSpecialties
          : specialtyArray;

        const displayedSpecialties =
          displaySource.slice(0, 2);

        const remainingCount = Math.max(
          0,
          displaySource.length -
            displayedSpecialties.length,
        );

        return (
          <div className="w-full px-2 py-1">
            <div className="flex flex-wrap justify-center items-center gap-1.5">
              {displayedSpecialties.map(
                (specialty, index) => {
                  const isMatching =
                    hasSearch &&
                    specialty.toLowerCase().includes(searchTerm);
                  const inactive = linkedInactive(specialtyEntries.find(spec => spec.name === specialty));

                  return (
                    <span
                      key={`${specialty}-${index}`}
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${
                        inactive ? "bg-gray-200 text-gray-600 border-gray-300" : isMatching
                          ? "bg-yellow-200 text-yellow-900 border-yellow-400"
                          : "bg-blue-100 text-blue-800 border-blue-200"
                      }`}
                    >
                      {specialty}{inactive ? " (Inactive)" : ""}
                    </span>
                  );
                },
              )}

              {remainingCount > 0 && (
                <button
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedSpecialties(
                      JSON.stringify(displaySource),
                    );
                    setSpecialtyDialogOpen(true);
                  }}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold text-white transition-colors ${
                    hasMatches
                      ? "bg-yellow-600 hover:bg-yellow-700"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  +{remainingCount} more
                </button>
              )}
            </div>

            <div className="mt-1 text-center text-[10px] text-gray-400">
              {hasMatches
                ? `${matchingSpecialties.length}/${specialtyArray.length}`
                : `${specialtyArray.length} total`}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: "encounter_checks",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          Encounter
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => (
        <span className="block text-center font-medium">
          {row.original.encounter_checks}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
          onClick={() =>
            column.toggleSorting(
              column.getIsSorted() === "asc",
            )
          }
        >
          Status
          {column.getIsSorted() === "asc"
            ? " ↑"
            : column.getIsSorted() === "desc"
              ? " ↓"
              : ""}
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;
        const isActive = isActiveStatus(item.status);
        const isDeleted = item.is_deleted;
        const statusValueStr = isActive ? "active" : "inactive";

        return (
          <div className="flex justify-center">
            <button
              data-testid={`provider-table-btn-status-toggle-${item.id}`}
              data-row-id={item.id}
              data-status={statusValueStr}
              onClick={() =>
                !isDeleted &&
                toggleProviderStatus(item)
              }
              disabled={!!isDeleted}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isDeleted
                  ? "opacity-40 cursor-not-allowed bg-gray-200"
                  : isActive
                    ? "bg-green-500"
                    : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isActive && !isDeleted
                    ? "translate-x-6"
                    : "translate-x-1"
                }`}
              />
            </button>
          </div>
        );
      },
    },
    {
      id: "view",
      header: () => (
        <Button
          variant="ghost"
          className="font-bold text-white w-full justify-center hover:bg-white/10 hover:text-white"
        >
          View
        </Button>
      ),
      cell: ({ row }) => {
        const item = row.original;

        return (
          <div className="flex justify-center items-center gap-2">
            <button
              data-testid={`provider-table-btn-view-details-${item.id}`}
              onClick={() => handleViewClick(item)}
              className="text-blue-600 hover:text-blue-800 transition-colors"
              title="View Details"
            >
              <i className="fa-solid fa-eye text-lg" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="h5-container mx-auto px-1 pt-16 pb-4" data-qa-page="provider">
      <div className="flex justify-between items-center">
        <h5 className="mb-6 flex items-center gap-2 font-bold text-2xl mt-4 text-blue-900" data-testid="provider-page-title">
          <span className="icon-container flex items-center justify-center w-10 h-10 rounded-full bg-blue-900 text-white">
            <i className="fa-solid fa-user-injured" />
          </span>
          Provider
        </h5>
      </div>
      <DataTable2
        qaIdPrefix="provider-data-table"
        columns={columns}
        data={items.result}
        searchColumns={[]}
        searchValue={filterName}
        onSearchChange={setFilterName}
        searchPlaceholder="Search..."
        searchHelpText="Provider ID, Provider Name, or Specialty."
        addButtonLabel="Add New Provider"
        showAddButton={true}
        compactToolbar={true}
        toolbarUpload={<FileUpload fetchItems={fetchItems} />}
        onEdit={handleEdit}
        pageIndex={currentPage}
        onPageChange={setCurrentPage}
        manualPagination={manualPagination}
        pageCount={totalPages}
        totalCount={totalCount}
        pageSize={pageSize}
        onPageSizeChange={(nextPageSize) => {
          setPageSize(nextPageSize);
          setCurrentPage(0);
        }}
        allowedPageSizes={allowedPageSizes}
        isLoading={isTableLoading}
        onDelete={(id) => {
          const item = items.result.find(
            (i) => i.id === id,
          );

          if ((item as any)?.is_deleted) {
            setForceId(id);
            setForceAction("hardDelete");
            setForceError("");
            setForceDialogOpen(true);
          } else {
            removeItem(id);
          }
        }}
        onRestore={
          canManageDeleted ? restoreItem : undefined
        }
        canHardDelete={canManageDeleted}
        onAdd={handleAddClick}
        onEditClick={handleEditClick}
        onDownload={handleDownload}
        isRowDeleted={(row: any) =>
          row.is_deleted === true
        }
        toolbarExtras={
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <ServerStatusPills
              value={filterStatus}
              onChange={(value) => {
                setFilterStatus(value);
                setCurrentPage(0);
              }}
            />

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b" }}>
                Encounter
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  padding: 3,
                  background: "#e2e8f0",
                  borderRadius: 9999,
                }}
              >
                {([
                  { value: "", label: "All" },
                  { value: "OP", label: "OP" },
                  { value: "IP", label: "IP" },
                  { value: "Both", label: "Both" },
                ] as const).map((option) => {
                  const selected = filterEncounter === option.value;

                  return (
                    <button
                      key={option.value || "all"}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => {
                        setFilterEncounter(option.value);
                        setCurrentPage(0);
                      }}
                      style={{
                        padding: "6px 12px",
                        fontSize: 12,
                        fontWeight: 500,
                        borderRadius: 9999,
                        border: "none",
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                        background: selected ? "#ffffff" : "transparent",
                        color: selected ? "#1d4ed8" : "#64748b",
                        boxShadow: selected
                          ? "0 2px 4px rgba(0,0,0,0.1)"
                          : "none",
                        transition: "all 0.15s ease",
                      }}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <ServerDeletedPills
              value={filterDeleted}
              onChange={(value) => {
                setFilterDeleted(value);
                setCurrentPage(0);
              }}
            />
          </div>
        }
      />

      {/* View Dialog */}
      {selectedItem && (
        <Dialog
          open={!!selectedItem}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedItem(null);
              setShowAllUpdates(false);
            }
          }}
        >
          <DialogContent
            data-testid="provider-dialog-view-details"
            ref={setDialogContentEl}
            className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
            style={{
              boxShadow:
                "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
              overflow: "visible",
            }}
          >
            {/* Header */}
            <div
              style={{
                background:
                  "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)",
                padding: "24px 28px 20px",
                flexShrink: 0,
                position: "relative",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: -40,
                  right: -40,
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.04)",
                }}
              />

              <div
                style={{
                  position: "absolute",
                  bottom: -30,
                  left: -20,
                  width: 100,
                  height: 100,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.03)",
                }}
              />

              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 16,
                  position: "relative",
                  zIndex: 1,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 14,
                    background: "rgba(255,255,255,0.12)",
                    backdropFilter: "blur(8px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                    color: "#fff",
                    flexShrink: 0,
                    border:
                      "1px solid rgba(255,255,255,0.1)",
                  }}
                >
                  <i className="fa-solid fa-user-injured" />
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      marginBottom: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    <h2
                      style={{
                        fontSize: 20,
                        fontWeight: 700,
                        color: "#fff",
                        margin: 0,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {selectedItem.provider_name}
                    </h2>

                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 12px",
                        borderRadius: 9999,
                        fontSize: 11,
                        fontWeight: 600,
                        flexShrink: 0,
                        background: isActiveStatus(
                          selectedItem.status,
                        )
                          ? "rgba(34,197,94,0.2)"
                          : "rgba(239,68,68,0.15)",
                        color: isActiveStatus(
                          selectedItem.status,
                        )
                          ? "#86efac"
                          : "#fca5a5",
                        border: `1px solid ${
                          isActiveStatus(
                            selectedItem.status,
                          )
                            ? "rgba(34,197,94,0.25)"
                            : "rgba(239,68,68,0.2)"
                        }`,
                      }}
                    >
                      <span
                        style={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          background: "currentColor",
                        }}
                      />
                      {isActiveStatus(selectedItem.status)
                        ? "Active"
                        : "Inactive"}
                    </span>

                    {selectedItem.is_deleted && (
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "3px 12px",
                          borderRadius: 9999,
                          fontSize: 11,
                          fontWeight: 600,
                          background:
                            "rgba(239,68,68,0.15)",
                          color: "#fca5a5",
                          border:
                            "1px solid rgba(239,68,68,0.2)",
                        }}
                      >
                        <i
                          className="fa-solid fa-trash"
                          style={{ fontSize: 10 }}
                        />
                        Deleted
                      </span>
                    )}
                  </div>

                  <p
                    style={{
                      fontSize: 12,
                      color: "rgba(147,197,253,0.8)",
                      margin: 0,
                    }}
                  >
                    Provider ID:{" "}
                    <span
                      style={{
                        fontWeight: 600,
                        color: "#93c5fd",
                      }}
                    >
                      #{selectedItem.provider_id}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <ScrollArea
              className="flex-1"
              style={{ background: "#f8fafc" }}
            >
              <div style={{ padding: "20px 28px 16px" }}>
                {/* Basic Info */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #dbeafe",
                    }}
                  >
                    <i
                      className="fa-solid fa-circle-info"
                      style={{
                        fontSize: 12,
                        color: "#3b82f6",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Basic Information
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 12,
                    marginBottom: 20,
                  }}
                >
                  {[
                    {
                      label: "Provider Name",
                      value: selectedItem.provider_name,
                      icon: "fa-user-injured",
                      color: "#3b82f6",
                    },
                    {
                      label: "Provider ID",
                      value: `#${selectedItem.provider_id}`,
                      icon: "fa-id-card",
                      color: "#8b5cf6",
                    },
                    {
                      label: "Encounter Checks",
                      value:
                        selectedItem.encounter_checks,
                      icon: "fa-stethoscope",
                      color: "#10b981",
                    },
                  ].map((field) => (
                    <div
                      key={field.label}
                      style={{
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                        boxShadow:
                          "0 1px 3px rgba(0,0,0,0.03)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 8,
                        }}
                      >
                        <div
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: 6,
                            background: `${field.color}12`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <i
                            className={`fa-solid ${field.icon}`}
                            style={{
                              fontSize: 10,
                              color: field.color,
                            }}
                          />
                        </div>

                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#94a3b8",
                            textTransform: "capitalize",
                            letterSpacing: "0.05em",
                          }}
                        >
                          {field.label}
                        </span>
                      </div>

                      <span
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        {field.value || "—"}
                      </span>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#e2e8f0",
                    margin: "0 0 20px",
                  }}
                />

                {/* Specialties */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <i
                      className="fa-solid fa-list-check"
                      style={{
                        fontSize: 12,
                        color: "#16a34a",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Specialties
                  </span>

                  <span
                    style={{
                      fontSize: 10,
                      padding: "2px 10px",
                      borderRadius: 9999,
                      background: "#3b82f6",
                      color: "#fff",
                      fontWeight: 700,
                    }}
                  >
                    {
                      getSpecialtyNames(
                        selectedItem.speciality,
                      ).length
                    }
                  </span>
                </div>

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    padding: "14px 16px",
                    marginBottom: 20,
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.03)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 8,
                    }}
                  >
                    {getSpecialtyNames(
                      selectedItem.speciality,
                    ).length === 0 ? (
                      <span
                        style={{
                          color: "#94a3b8",
                          fontSize: 12,
                          fontStyle: "italic",
                        }}
                      >
                        No specialties available
                      </span>
                    ) : (
                      linkedSpecialties(selectedItem.speciality).map((spec, idx) => (
                        <span
                          key={`${spec}-${idx}`}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "4px 14px",
                            borderRadius: 9999,
                            fontSize: 12,
                            fontWeight: 500,
                            background: linkedInactive(spec) ? "#e5e7eb" : "#eff6ff",
                            color: linkedInactive(spec) ? "#4b5563" : "#1d4ed8",
                            border: linkedInactive(spec) ? "1px solid #9ca3af" : "1px solid #bfdbfe",
                          }}
                        >
                          {spec.name}{linkedInactive(spec) ? " (Inactive)" : ""}
                        </span>
                      ))
                    )}
                  </div>
                </div>

                <div
                  style={{
                    height: 1,
                    background: "#e2e8f0",
                    margin: "0 0 20px",
                  }}
                />

                {/* Audit Trail */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 8,
                      background: "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <i
                      className="fa-solid fa-clock-rotate-left"
                      style={{
                        fontSize: 12,
                        color: "#16a34a",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Audit Trail
                  </span>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr 1fr",
                    gap: 12,
                  }}
                >
                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "14px 16px",
                      boxShadow:
                        "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#94a3b8",
                      }}
                    >
                      Created By
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        marginTop: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 32,
                          height: 32,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(135deg, #3b82f6, #2563eb)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#fff",
                          flexShrink: 0,
                        }}
                      >
                        {String(
                          resolveUserName(
                            (selectedItem as any)
                              .created_by,
                          ) ?? "?",
                        )[0].toUpperCase()}
                      </div>

                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        {resolveUserName(
                          (selectedItem as any)
                            .created_by,
                        )}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "14px 16px",
                      boxShadow:
                        "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#94a3b8",
                      }}
                    >
                      Created On
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 10,
                      }}
                    >
                      <i
                        className="fa-regular fa-calendar"
                        style={{
                          fontSize: 14,
                          color: "#3b82f6",
                        }}
                      />

                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        {(selectedItem as any).createdat
                          ? formatDisplayDateTime((selectedItem as any).createdat)
                          : "—"}
                      </span>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#fff",
                      borderRadius: 12,
                      border: "1px solid #e2e8f0",
                      padding: "14px 16px",
                      boxShadow:
                        "0 1px 3px rgba(0,0,0,0.03)",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 600,
                        color: "#94a3b8",
                      }}
                    >
                      Last Updated
                    </span>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 10,
                      }}
                    >
                      <i
                        className="fa-regular fa-clock"
                        style={{
                          fontSize: 14,
                          color: "#10b981",
                        }}
                      />

                      <span
                        style={{
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#1e293b",
                        }}
                      >
                        {(selectedItem as any).updatedat
                          ? formatDisplayDateTime((selectedItem as any).updatedat)
                          : "—"}
                      </span>
                    </div>
                  </div>
                </div>

                {Array.isArray(
                  (selectedItem as any).updated_by,
                ) &&
                  (selectedItem as any).updated_by
                    .length > 0 && (
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e2e8f0",
                        padding: "14px 16px",
                        boxShadow:
                          "0 1px 3px rgba(0,0,0,0.03)",
                        marginTop: 12,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          marginBottom: 12,
                        }}
                      >
                        <i
                          className="fa-solid fa-pen-to-square"
                          style={{
                            fontSize: 11,
                            color: "#10b981",
                          }}
                        />

                        <span
                          style={{
                            fontSize: 10,
                            fontWeight: 600,
                            color: "#94a3b8",
                            textTransform: "uppercase",
                            letterSpacing: "0.02em",
                          }}
                        >
                          Update History
                        </span>

                        <span
                          style={{
                            fontSize: 10,
                            padding: "1px 8px",
                            borderRadius: 9999,
                            background: "#f0fdf4",
                            color: "#15803d",
                            fontWeight: 600,
                            border:
                              "1px solid #bbf7d0",
                            marginLeft: "auto",
                          }}
                        >
                          {
                            (selectedItem as any)
                              .updated_by.length
                          }{" "}
                          update
                          {(selectedItem as any)
                            .updated_by.length !== 1
                            ? "s"
                            : ""}
                        </span>
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {(
                          (selectedItem as any)
                            .updated_by as {
                            id?: number;
                            user_id?: number;
                            at?: string;
                            date?: string;
                            [key: string]: any;
                          }[]
                        )
                          .slice()
                          .reverse()
                          .slice(
                            0,
                            showAllUpdates
                              ? undefined
                              : 4,
                          )
                          .map((entry, i) => {
                            const updateDate = entry.date || entry.at || (entry as any).updatedat || (entry as any).timestamp || (i === 0 ? (selectedItem as any).updatedat : undefined);
                            return (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent:
                                  "space-between",
                                padding: "8px 12px",
                                borderRadius: 8,
                                background:
                                  i === 0
                                    ? "#f0fdf4"
                                    : "#f8fafc",
                                border: `1px solid ${
                                  i === 0
                                    ? "#bbf7d0"
                                    : "#e2e8f0"
                                }`,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  alignItems:
                                    "center",
                                  gap: 8,
                                }}
                              >
                                <div
                                  style={{
                                    width: 28,
                                    height: 28,
                                    borderRadius:
                                      "50%",
                                    background:
                                      i === 0
                                        ? "linear-gradient(135deg, #10b981, #059669)"
                                        : "linear-gradient(135deg, #94a3b8, #64748b)",
                                    display:
                                      "flex",
                                    alignItems:
                                      "center",
                                    justifyContent:
                                      "center",
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: "#fff",
                                    flexShrink: 0,
                                  }}
                                >
                                  {String(
                                    resolveUserName(
                                      entry.id ?? entry.user_id,
                                    ) ?? "?",
                                  )[0].toUpperCase()}
                                </div>

                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: "#1e293b",
                                  }}
                                >
                                  {resolveUserName(
                                    entry.id ?? entry.user_id,
                                  )}
                                </span>

                                {i === 0 && (
                                  <span
                                    style={{
                                      fontSize: 10,
                                      padding:
                                        "1px 8px",
                                      borderRadius:
                                        9999,
                                      background:
                                        "#dcfce7",
                                      color:
                                        "#15803d",
                                      fontWeight:
                                        600,
                                      border:
                                        "1px solid #bbf7d0",
                                    }}
                                  >
                                    Latest
                                  </span>
                                )}
                              </div>

                              <span
                                style={{
                                  fontSize: 11,
                                  color: "#64748b",
                                  fontWeight: 500,
                                }}
                              >
                                {updateDate
                                  ? formatDisplayDateTime(updateDate)
                                  : "—"}
                              </span>
                            </div>
                          );})}

                        {(selectedItem as any)
                          .updated_by.length > 4 && (
                          <div
                            style={{
                              textAlign: "center",
                              padding: "6px 0",
                            }}
                          >
                            <button
                              onClick={() =>
                                setShowAllUpdates(
                                  (v) => !v,
                                )
                              }
                              style={{
                                fontSize: 11,
                                color: "#3b82f6",
                                fontWeight: 600,
                                background:
                                  "none",
                                border: "none",
                                cursor: "pointer",
                              }}
                            >
                              {showAllUpdates
                                ? "Show less ↑"
                                : `+${
                                    (selectedItem as any)
                                      .updated_by
                                      .length - 4
                                  } more updates ↓`}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                <div style={{ paddingBottom: 8 }} />
              </div>
            </ScrollArea>

            {/* Footer */}
            <div
              style={{
                padding: "14px 28px",
                borderTop: "1px solid #e2e8f0",
                background: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                flexShrink: 0,
              }}
            >
              <Button
                data-testid="provider-btn-close-view-details"
                onClick={() => {
                  setSelectedItem(null);
                  setShowAllUpdates(false);
                }}
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a8a, #2563eb)",
                  color: "#fff",
                  fontWeight: 600,
                  padding: "8px 28px",
                  borderRadius: 10,
                  boxShadow:
                    "0 2px 8px rgba(37,99,235,0.3)",
                }}
              >
                Close Details
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Specialty Dialog */}
      <Dialog
        open={specialtyDialogOpen}
        onOpenChange={setSpecialtyDialogOpen}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-blue-900 flex items-center gap-2">
              <i className="fa-solid fa-list-check" />
              All Specialties
            </DialogTitle>
          </DialogHeader>

          <div className="mt-4">
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <div
                className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto pr-2"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "#3b82f6 #dbeafe",
                }}
              >
                {getSpecialtyNames(
                  selectedSpecialties,
                ).map((specialty, index) => (
                  <div
                    key={`${specialty}-${index}`}
                    className={`flex items-center gap-3 p-3 rounded-lg shadow-sm border ${linkedInactive(linkedSpecialties([{name: specialty}])[0]) ? "bg-gray-200 border-gray-300 text-gray-600" : "bg-white border-blue-100"}`}
                  >
                    <div className="flex items-center justify-center w-8 h-8 bg-blue-600 text-white rounded-full flex-shrink-0">
                      <i className="fa-solid fa-user-doctor text-sm" />
                    </div>

                    <span className="text-gray-800 font-medium flex-1">
                      {specialty}{linkedInactive(linkedSpecialties([{name: specialty}])[0]) ? " (Inactive)" : ""}
                    </span>

                    <span className="text-xs text-gray-500 bg-blue-100 px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              onClick={() =>
                setSpecialtyDialogOpen(false)
              }
              className="bg-blue-600 hover:bg-blue-700"
            >
              <i className="fa-solid fa-check mr-2" />
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Provider Dialog */}
      <Dialog
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
      >
        <DialogContent
          data-testid={editIndex === null ? "provider-dialog-add-provider" : "provider-dialog-edit-provider"}
          ref={setDialogContentEl}
          className="max-w-[640px] max-h-[90vh] flex flex-col p-0 bg-white border-none rounded-2xl"
          onInteractOutside={(event: any) => {
            const target =
              event.target as HTMLElement;

            if (
              target.closest(
                ".specialty-select__menu",
              ) ||
              target.closest(
                ".specialty-select__menu-portal",
              )
            ) {
              event.preventDefault();
            }
          }}
          style={{
            boxShadow:
              "0 25px 60px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)",
            overflow: "visible",
          }}
        >
          <div
            style={{
              background:
                "linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #2563eb 100%)",
              padding: "22px 28px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 140,
                height: 140,
                borderRadius: "50%",
                background: "rgba(255,255,255,0.04)",
              }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: 12,
                  background:
                    "rgba(255,255,255,0.12)",
                  backdropFilter: "blur(8px)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                  color: "#fff",
                  flexShrink: 0,
                  border:
                    "1px solid rgba(255,255,255,0.1)",
                }}
              >
                <i
                  className={`fa-solid ${
                    editIndex === null
                      ? "fa-plus"
                      : "fa-pen"
                  }`}
                />
              </div>

              <div>
                <h2
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#fff",
                    margin: 0,
                  }}
                >
                  {editIndex === null
                    ? "Add Provider"
                    : "Edit Provider"}
                </h2>
              </div>
            </div>
          </div>

          <form
            onSubmit={onSubmit}
            className="flex flex-col flex-1 min-h-0"
          >
            <ScrollArea
              className="flex-1"
              style={{ background: "#f8fafc" }}
            >
              <div
                style={{
                  padding: "22px 28px 32px",
                }}
              >
                {gloableError && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      background: "#fef2f2",
                      border:
                        "1px solid #fecaca",
                      borderRadius: 10,
                      padding: "10px 14px",
                      marginBottom: 18,
                    }}
                  >
                    <i
                      className="fa-solid fa-circle-exclamation"
                      style={{
                        color: "#dc2626",
                        marginTop: 2,
                        fontSize: 12,
                      }}
                    />

                    <p
                      style={{
                        fontSize: 13,
                        color: "#991b1b",
                        margin: 0,
                      }}
                    >
                      {gloableError}
                    </p>
                  </div>
                )}

                {/* Basic Information */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: "#eff6ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #dbeafe",
                    }}
                  >
                    <i
                      className="fa-solid fa-circle-info"
                      style={{
                        fontSize: 11,
                        color: "#3b82f6",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Basic Information
                  </span>
                </div>

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    padding: "18px",
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.03)",
                    marginBottom: 18,
                  }}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label
                        htmlFor="name"
                        className="text-xs font-semibold text-slate-500"
                      >
                        Provider Name <span className="text-orange-500 font-bold" aria-hidden="true">*</span>
                      </Label>

                      <Input
                        data-testid="provider-input-provider-name"
                        id="name"
                        value={formName}
                        onChange={(e) =>
                          setFormName(
                            e.target.value,
                          )
                        }
                        placeholder="Enter provider name"
                        className={`mt-1.5 ${
                          formErrors.name
                            ? "border-red-400 focus-visible:ring-red-300"
                            : ""
                        }`}
                      />

                      {formErrors.name && (
                        <p data-testid="provider-message-error-provider-name" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="fa-solid fa-circle-exclamation text-[10px]" />
                          {formErrors.name}
                        </p>
                      )}
                    </div>

                    <div>
                      <Label
                        htmlFor="providerId"
                        className="text-xs font-semibold text-slate-500"
                      >
                        Provider ID <span className="text-orange-500 font-bold" aria-hidden="true">*</span>
                      </Label>

                      <Input
                        data-testid="provider-input-provider-id"
                        id="providerId"
                        value={formProviderId}
                        onChange={(e) =>
                          setFormProviderId(
                            e.target.value,
                          )
                        }
                        placeholder="Enter provider ID"
                        className={`mt-1.5 ${
                          formErrors.providerId
                            ? "border-red-400 focus-visible:ring-red-300"
                            : ""
                        }`}
                      />
                      {formErrors.providerId && (
                        <p className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                          <i className="fa-solid fa-circle-exclamation text-[10px]" />
                          {formErrors.providerId}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Configuration */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: "#f0fdf4",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #bbf7d0",
                    }}
                  >
                    <i
                      className="fa-solid fa-sliders"
                      style={{
                        fontSize: 11,
                        color: "#16a34a",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Configuration
                  </span>
                </div>

                <div
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: "1px solid #e2e8f0",
                    padding: "18px",
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.03)",
                    marginBottom: 18,
                  }}
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs font-semibold text-slate-500">
                        Encounter
                      </Label>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        {(
                          [
                            "OP",
                            "IP",
                            "Both",
                          ] as const
                        ).map((val) => {
                          const encounterTestId = 
                            val === "OP" ? "provider-btn-encounter-op" :
                            val === "IP" ? "provider-btn-encounter-ip" :
                            "provider-btn-encounter-both";

                          return (
                            <button
                              data-testid={encounterTestId}
                              type="button"
                              key={val}
                              onClick={() =>
                                setFormEncounter(
                                  val,
                                )
                              }
                              style={{
                                flex: 1,
                                padding: "8px 0",
                                borderRadius: 8,
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                border:
                                  formEncounter ===
                                  val
                                    ? "1px solid #2563eb"
                                    : "1px solid #e2e8f0",
                                background:
                                  formEncounter ===
                                  val
                                    ? "#eff6ff"
                                    : "#fff",
                                color:
                                  formEncounter ===
                                  val
                                    ? "#1d4ed8"
                                    : "#64748b",
                                transition:
                                  "all 0.15s",
                              }}
                            >
                              {val}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <Label className="text-xs font-semibold text-slate-500">
                        Status
                      </Label>

                      <div
                        style={{
                          display: "flex",
                          gap: 8,
                          marginTop: 8,
                        }}
                      >
                        <button
                          data-testid="provider-btn-status-active"
                          type="button"
                          onClick={() =>
                            setFormActive(true)
                          }
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                              "center",
                            gap: 6,
                            padding: "8px 0",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            border: formActive
                              ? "1px solid #16a34a"
                              : "1px solid #e2e8f0",
                            background:
                              formActive
                                ? "#f0fdf4"
                                : "#fff",
                            color: formActive
                              ? "#15803d"
                              : "#64748b",
                            transition:
                              "all 0.15s",
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius:
                                "50%",
                              background:
                                "currentColor",
                            }}
                          />
                          Active
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            setFormActive(false)
                          }
                          style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            justifyContent:
                              "center",
                            gap: 6,
                            padding: "8px 0",
                            borderRadius: 8,
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: "pointer",
                            border: !formActive
                              ? "1px solid #dc2626"
                              : "1px solid #e2e8f0",
                            background:
                              !formActive
                                ? "#fef2f2"
                                : "#fff",
                            color: !formActive
                              ? "#b91c1c"
                              : "#64748b",
                            transition:
                              "all 0.15s",
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius:
                                "50%",
                              background:
                                "currentColor",
                            }}
                          />
                          Inactive
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Specialty */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 7,
                      background: "#faf5ff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid #e9d5ff",
                    }}
                  >
                    <i
                      className="fa-solid fa-list-check"
                      style={{
                        fontSize: 11,
                        color: "#9333ea",
                      }}
                    />
                  </div>

                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: "#0f172a",
                    }}
                  >
                    Specialty
                  </span>
                </div>

                <div
                  data-testid="provider-select-specialties-wrapper"
                  style={{
                    background: "#fff",
                    borderRadius: 12,
                    border: `1px solid ${
                      formErrors.specialty
                        ? "#fca5a5"
                        : "#e2e8f0"
                    }`,
                    padding: "18px",
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.03)",
                  }}
                >
                  <Label className="text-xs font-semibold text-slate-500">
                    Select Specialties <span className="text-orange-500 font-bold" aria-hidden="true">*</span>
                  </Label>

                  <div className="mt-1.5">
                    <SpecialtyProviderSelector
                      onChange={(val) => {
                        const locked = formSpecialty.filter((old: any) => linkedInactive(old) && !val.some(next => String(next.value) === String(old.value)));
                        setFormSpecialty([...val, ...locked]);
                        setFormErrors((e) => ({
                          ...e,
                          specialty:
                            undefined,
                        }));
                      }}
                      value={formSpecialty}
                      clear={false}
                    />
                  </div>

                  {formErrors.specialty && (
                    <p data-testid="provider-message-error-specialty" className="text-red-500 text-xs mt-1.5 flex items-center gap-1">
                      <i className="fa-solid fa-circle-exclamation text-[10px]" />
                      {formErrors.specialty}
                    </p>
                  )}
                </div>
              </div>
            </ScrollArea>

            {/* Footer */}
            <div
              style={{
                padding: "16px 28px",
                borderTop: "1px solid #e2e8f0",
                background: "#fff",
                display: "flex",
                justifyContent: "flex-end",
                gap: 10,
              }}
            >
              <Button
                data-testid={editIndex === null ? "provider-btn-cancel-add-provider" : "provider-btn-cancel-edit-provider"}
                type="button"
                variant="outline"
                onClick={() => {
                  setEditIndex(null);
                  clearForm();
                  setIsDialogOpen(false);
                }}
                style={{
                  borderRadius: 10,
                  fontWeight: 600,
                }}
              >
                Cancel
              </Button>

              <Button
                data-testid={editIndex === null ? "provider-btn-add-provider-submit" : "provider-btn-update-provider-submit"}
                type="submit"
                disabled={Boolean(
                  getProviderNameValidationError(formName) ||
                  formErrors.name ||
                  formErrors.providerId ||
                  formErrors.specialty
                )}
                style={{
                  background:
                    "linear-gradient(135deg, #1e3a8a, #2563eb)",
                  color: "#fff",
                  fontWeight: 600,
                  padding: "8px 24px",
                  borderRadius: 10,
                  boxShadow:
                    "0 2px 8px rgba(37,99,235,0.3)",
                  opacity: Boolean(
                    getProviderNameValidationError(formName) ||
                    formErrors.name ||
                    formErrors.providerId ||
                    formErrors.specialty
                  ) ? 0.55 : 1,
                  cursor: Boolean(
                    getProviderNameValidationError(formName) ||
                    formErrors.name ||
                    formErrors.providerId ||
                    formErrors.specialty
                  ) ? "not-allowed" : "pointer",
                }}
              >
                <i
                  className={`fa-solid ${
                    editIndex === null
                      ? "fa-plus"
                      : "fa-check"
                  } mr-2`}
                />
                {editIndex === null
                  ? "Add Provider"
                  : "Update Provider"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Force / Hard Delete Dialog */}
      {forceDialogOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "#00000088",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 16,
              padding: 32,
              width: 440,
              boxShadow:
                "0 20px 50px rgba(0,0,0,0.3)",
              border: "1px solid #fecaca",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                gap: 12,
              }}
            >
              <div
                style={{
                  width: 60,
                  height: 60,
                  borderRadius: "50%",
                  background: "#fef2f2",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className="fa-solid fa-triangle-exclamation"
                  style={{
                    fontSize: 28,
                    color: "#dc2626",
                  }}
                />
              </div>

              {/* Hard Delete */}
              {forceAction === "hardDelete" && (
                <>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#1e293b",
                      margin: 0,
                    }}
                  >
                    Permanent Delete
                  </h3>

                  <p
                    style={{
                      fontSize: 13,
                      color: "#64748b",
                      margin: 0,
                      lineHeight: 1.6,
                    }}
                  >
                    This will{" "}
                    <strong
                      style={{
                        color: "#dc2626",
                      }}
                    >
                      permanently delete
                    </strong>{" "}
                    the provider from the database.
                    <br />
                    This action{" "}
                    <strong>
                      cannot be undone
                    </strong>
                    .
                  </p>

                  {forceError && (
                    <div
                      style={{
                        width: "100%",
                        background: "#fef9c3",
                        border:
                          "1px solid #fde047",
                        borderRadius: 10,
                        padding: "12px 16px",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems:
                            "flex-start",
                          gap: 8,
                        }}
                      >
                        <i
                          className="fa-solid fa-circle-info"
                          style={{
                            color: "#ca8a04",
                            marginTop: 2,
                            flexShrink: 0,
                          }}
                        />

                        <p
                          style={{
                            fontSize: 13,
                            color: "#713f12",
                            margin: 0,
                            lineHeight: 1.6,
                          }}
                        >
                          {forceError}
                        </p>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      width: "100%",
                      marginTop: 4,
                    }}
                  >
                    <button
                      onClick={() => {
                        setForceDialogOpen(false);
                        setForceId(null);
                        setForceError("");
                        setForceAction(null);
                      }}
                      style={{
                        flex: 1,
                        background: "#f1f5f9",
                        color: "#475569",
                        border:
                          "1px solid #e2e8f0",
                        borderRadius: 9,
                        padding: 12,
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() =>
                        forceId !== null &&
                        permanentDeleteItem(
                          forceId,
                          true,
                        )
                      }
                      style={{
                        flex: 1,
                        background: "#7f1d1d",
                        color: "#fff",
                        border: "none",
                        borderRadius: 9,
                        padding: 12,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      <i className="fa-solid fa-bolt mr-2" />
                      Force Delete Anyway
                    </button>
                  </div>
                </>
              )}

              {/* Soft Delete / Status */}
              {(forceAction === "softDelete" ||
                forceAction === "status") && (
                <>
                  <h3
                    style={{
                      fontSize: 18,
                      fontWeight: 700,
                      color: "#1e293b",
                      margin: 0,
                    }}
                  >
                    {forceAction === "status"
                      ? "Status Change Blocked"
                      : "Provider Is In Use"}
                  </h3>

                  <div
                    style={{
                      width: "100%",
                      background: "#fef9c3",
                      border:
                        "1px solid #fde047",
                      borderRadius: 10,
                      padding: "12px 16px",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems:
                          "flex-start",
                        gap: 8,
                      }}
                    >
                      <i
                        className="fa-solid fa-circle-info"
                        style={{
                          color: "#ca8a04",
                          marginTop: 2,
                          flexShrink: 0,
                        }}
                      />

                      <p
                        style={{
                          fontSize: 13,
                          color: "#713f12",
                          margin: 0,
                          lineHeight: 1.6,
                        }}
                      >
                        {forceError}
                      </p>
                    </div>
                  </div>

                  <p
                    style={{
                      fontSize: 13,
                      color: "#64748b",
                      margin: 0,
                    }}
                  >
                    Do you want to{" "}
                    <strong
                      style={{
                        color: "#dc2626",
                      }}
                    >
                      force{" "}
                      {forceAction === "status"
                        ? "update status"
                        : "delete"}
                    </strong>{" "}
                    anyway?
                  </p>

                  <div
                    style={{
                      display: "flex",
                      gap: 10,
                      width: "100%",
                      marginTop: 4,
                    }}
                  >
                    <button
                      onClick={() => {
                        setForceDialogOpen(false);
                        setForceId(null);
                        setForceError("");
                        setForceAction(null);
                      }}
                      style={{
                        flex: 1,
                        background: "#f1f5f9",
                        color: "#475569",
                        border:
                          "1px solid #e2e8f0",
                        borderRadius: 9,
                        padding: 12,
                        cursor: "pointer",
                        fontWeight: 600,
                        fontSize: 13,
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={() => {
                        if (!forceId) return;

                        if (
                          forceAction ===
                          "softDelete"
                        ) {
                          forceSoftDelete(forceId);
                        } else if (
                          forceAction === "status"
                        ) {
                          const item =
                            items.result.find(
                              (i) =>
                                i.id ===
                                forceId,
                            );

                          if (item) {
                            toggleProviderStatus(
                              item,
                              true,
                            );
                          }
                        }
                      }}
                      style={{
                        flex: 1,
                        background: "#dc2626",
                        color: "#fff",
                        border: "none",
                        borderRadius: 9,
                        padding: 12,
                        cursor: "pointer",
                        fontWeight: 700,
                        fontSize: 13,
                      }}
                    >
                      <i className="fa-solid fa-bolt mr-2" />
                      Force{" "}
                      {forceAction === "status"
                        ? "Update"
                        : "Delete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <Toaster />
    </div>
  );
};

export default Provider;